import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MsalService } from '@azure/msal-angular';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { LocalStorageService } from './local-storage.service';
import { DRAFT_PERSISTANT_CONFIG } from './persistence.interface';
import { SessionManagerService } from './session-manager.service';
import { StoredEntityData } from './storage.interface';

@Injectable()
export class FormPersistenceService implements OnDestroy {
	private readonly authService = inject(MsalService);
	private readonly localStorageService = inject(LocalStorageService);
	private readonly config = inject(DRAFT_PERSISTANT_CONFIG);
	private readonly sessionManagerService = inject(SessionManagerService);
	private readonly userId = signal<string>('');
	private readonly autoSaveSubject = new Subject<{
		form: FormGroup;
		entityType: string;
		entityId: string;
		phase?: string;
	}>();
	private readonly destroy$ = new Subject<void>();

	public constructor() {
		this.autoSaveSubject
			.pipe(
				debounceTime(1000), // Auto-save after 1 second of inactivity
				takeUntil(this.destroy$),
			)
			.subscribe(({ form, entityType, entityId, phase }) => {
				this.saveDraft(entityType, entityId, form.getRawValue(), phase);
			});
		const accounts = this.authService.instance.getAllAccounts();
		if (!accounts || accounts.length === 0) {
			console.warn('No active user account found. Cannot save draft.');
			return;
		}
		const userId = accounts[0].localAccountId;
		this.userId.set(userId);
	}

	public ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Triggers the auto-save process for a form.
	 * @param form The form to save.
	 * @param entityType The type of the entity.
	 * @param entityId The ID of the entity.
	 * @param phase An optional phase for multi-part forms.
	 */
	public autoSave(form: FormGroup, entityType: string, entityId: string, phase?: string): void {
		this.autoSaveSubject.next({ form, entityType, entityId, phase });
	}

	/**
	 * Saves a form draft to local storage.
	 * @param entityType The type of the entity.
	 * @param entityId The ID of the entity.
	 * @param formData The data from the form.
	 * @param phase An optional phase for multi-part forms.
	 */
	public saveDraft(entityType: string, entityId: string, formData: unknown, phase?: string) {
		const sessionId = this.sessionManagerService.getSessionId();
		if (!sessionId) return;

		const key = this.localStorageService.generateDraftKey(this.userId(), entityType, entityId);
		const now = Date.now();
		const ttl = this.config.ttl * 1000;

		const data: StoredEntityData = {
			metadata: {
				userId: this.userId(),
				sessionId,
				createdAt: this.localStorageService.getDraft(key)?.metadata.createdAt ?? now,
				lastModified: now,
				expiresAt: now + ttl,
				version: 1,
			},
			formData,
			entityType,
			entityId,
			phase,
			isDirty: true,
			autoSaveEnabled: true,
		};

		this.localStorageService.setDraft(key, data);
		this.updateUserIndex(this.userId(), key);
	}

	/**
	 * Loads a form draft from local storage.
	 * @param entityType The type of the entity.
	 * @param entityId The ID of the entity.
	 * @returns The stored entity data, or null if not found.
	 */
	public loadDraft<T>(entityType: string, entityId: string) {
		const key = this.localStorageService.generateDraftKey(this.userId(), entityType, entityId);
		return this.localStorageService.getDraft<T>(key);
	}

	/**
	 * Deletes a form draft from local storage.
	 * @param entityType The type of the entity.
	 * @param entityId The ID of the entity.
	 */
	public deleteDraft(entityType: string, entityId: string) {
		const key = this.localStorageService.generateDraftKey(this.userId(), entityType, entityId);
		this.localStorageService.removeItem(key);
		this.removeDraftFromIndex(this.userId(), key);
	}

	/**
	 * Checks if a draft exists for a given entity.
	 * @param entityType The type of the entity.
	 * @param entityId The ID of the entity.
	 * @returns True if a draft exists, false otherwise.
	 */
	public hasDraft(entityType: string, entityId: string) {
		return !!this.loadDraft(entityType, entityId);
	}

	/**
	 * Deletes all drafts for the current user.
	 */
	public deleteAllDrafts() {
		const userId = this.userId();
		const index = this.localStorageService.getUserDraftIndex(userId);
		if (!index) return;
		index.draftKeys.forEach((key) => {
			this.localStorageService.removeItem(key);
		});
		index.draftKeys = [];
		index.lastActivity = Date.now();
		this.localStorageService.setUserDraftIndex(userId, index);
	}

	private updateUserIndex(userId: string, draftKey: string) {
		const index = this.localStorageService.getUserDraftIndex(userId);
		if (index && !index.draftKeys.includes(draftKey)) {
			index.draftKeys.push(draftKey);
			index.lastActivity = Date.now();
			this.localStorageService.setUserDraftIndex(userId, index);
		}
	}

	private removeDraftFromIndex(userId: string, draftKey: string) {
		const index = this.localStorageService.getUserDraftIndex(userId);
		if (!index) return;
		index.draftKeys = index.draftKeys.filter((k) => k !== draftKey);
		index.lastActivity = Date.now();
		this.localStorageService.setUserDraftIndex(userId, index);
	}
}
